import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';

export interface RafflePlayerProps {
  /** 비디오 URL */
  videoUrl: string;
  /** 포스터 이미지 URL */
  poster?: string;
}

export function RafflePlayer({ videoUrl, poster }: RafflePlayerProps): ReactElement {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const isPlaying = useRef(false);

  const play = useCallback(() => {
    if (!video) { return; }
    if (isPlaying.current) { return; }

    video.play();
    isPlaying.current = true;
  }, [video]);

  const pause = useCallback(() => {
    if (!video) { return; }
    if (!isPlaying.current) { return; }
    video.pause();
    isPlaying.current = false;
  }, [video]);

  const stop = useCallback(() => {
    if (!video) { return; }
    video.currentTime = 0;
    video.pause();
    isPlaying.current = false;
  }, [video]);

  function handleEnded() {
    isPlaying.current = false;
  }

  useEffect(() => {
    if (videoUrl) { play(); }
  }, [videoUrl, play]);

  useEffect(() => {
    if (videoUrl) {
      play();
    } else {
      stop();
    }
  }, [videoUrl, play, stop]);

  useIntersectionObserver({
    target: video,
    onIntersect: () => videoUrl && play(),
    onEscape: pause,
  });

  useEffect(() => {
    function handlePageShow() {
      if (isPlaying.current) { play(); }
    }
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [play]);

  return (
    <Player
      preload="metadata"
      ref={(el) => setVideo(el)}
      src={`${videoUrl}#t=0.5`}
      poster={poster}
      loop
      muted
      controls={false}
      onEnded={handleEnded}
      playsInline
    />
  );
}

const Player = styled.video`
  width: 100%;
  display: block;
  aspect-ratio: 375 / 550;
`;
